/**
 * Display-Only Currency Conversion Engine
 * Converts displayed prices without affecting backend or cart values.
 */

window.CurrencyEngine = (function() {
  const STORAGE_KEY = 'currency_preference';
  const RATES_KEY = 'currency_rates_cache';
  const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

  let config = {
    enabled: false,
    autoDetect: false,
    defaultCurrency: 'USD',
    currentCurrency: 'USD',
    exchangeRates: {},
    supportedCurrencies: []
  };
  
  let isInitialized = false;
  let observer = null;

  /**
   * Initialize the engine with theme settings.
   */
  async function init(options) {
    config = Object.assign({}, config, options);
    if (config.supportedCurrencies) {
      config.supportedCurrencies = config.supportedCurrencies.map(c => (c || '').trim());
    }
    if (!config.enabled) return;

    // 1. Load cached rates if available and not expired
    let cachedRates = null;
    try {
      const stored = localStorage.getItem(RATES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Date.now() - parsed.timestamp < CACHE_EXPIRY) {
          cachedRates = parsed.rates;
          config.exchangeRates = cachedRates;
        }
      }
    } catch (e) {
      console.warn('CurrencyEngine: Failed to read cached rates');
    }

    // 2. Prepare rates fetch (only if cache is missing/expired)
    let ratesPromise = null;
    if (!cachedRates) {
      ratesPromise = fetch('https://download.softwarediscountusa.com/api/exchange-rates')
        .then(res => res.json())
        .then(data => {
          if (data && data.success && data.data) {
            const newRates = {};
            data.data.forEach(item => {
              newRates[item.currency_code] = parseFloat(item.rate_vs_usd);
            });
            config.exchangeRates = newRates;
            localStorage.setItem(RATES_KEY, JSON.stringify({
              timestamp: Date.now(),
              rates: newRates
            }));
          }
        })
        .catch(e => console.error('CurrencyEngine: Failed to fetch exchange rates', e));
    }

    // 3. Determine user's currency preference
    const savedCurrency = localStorage.getItem(STORAGE_KEY);
    let ipapiPromise = null;
    
    if (savedCurrency && config.supportedCurrencies.includes(savedCurrency)) {
      config.currentCurrency = savedCurrency;
    } else if (config.autoDetect) {
      // Fetch from IPAPI and save to prevent future calls
      ipapiPromise = fetch('https://ipapi.co/currency/')
        .then(res => res.text())
        .then(code => {
          const trimmedCode = code ? code.trim() : '';
          if (trimmedCode && config.supportedCurrencies.includes(trimmedCode)) {
            config.currentCurrency = trimmedCode;
            localStorage.setItem(STORAGE_KEY, trimmedCode); // Cache detection
          } else {
            config.currentCurrency = config.defaultCurrency;
          }
        })
        .catch(() => {
          config.currentCurrency = config.defaultCurrency;
        });
    } else {
      config.currentCurrency = config.defaultCurrency;
    }

    // 4. Wait for any pending network requests (running in parallel)
    const promisesToWait = [];
    if (ratesPromise) promisesToWait.push(ratesPromise);
    if (ipapiPromise) promisesToWait.push(ipapiPromise);
    
    if (promisesToWait.length > 0) {
      await Promise.all(promisesToWait);
    }
    
    // Ensure fallback is valid if exchange rate is missing for the selected currency
    if (config.currentCurrency !== config.defaultCurrency && !config.exchangeRates[config.currentCurrency]) {
        config.currentCurrency = config.defaultCurrency;
    }

    isInitialized = true;
    
    // Initial Conversion
    convertContainer(document.body);
    
    // Sync all dropdown selectors and dispatch event
    document.querySelectorAll('.currency-engine-select').forEach(select => {
      select.value = config.currentCurrency;
    });
    document.dispatchEvent(new CustomEvent('currency:changed', { detail: config.currentCurrency }));
    
    // Setup Mutation Observer for dynamic elements
    setupObserver();
  }

  /**
   * Set a new currency and convert all prices on the page.
   */
  function setCurrency(code) {
    if (!config.enabled) return;
    
    config.currentCurrency = code;
    localStorage.setItem(STORAGE_KEY, code);
    
    convertContainer(document.body);

    // Sync all dropdown selectors and dispatch event
    document.querySelectorAll('.currency-engine-select').forEach(select => {
      select.value = code;
    });
    document.dispatchEvent(new CustomEvent('currency:changed', { detail: code }));
  }

  function getCurrency() {
    return config.currentCurrency;
  }

  function refresh() {
    convertContainer(document.body);
  }

  /**
   * Recursively find and convert all elements with data-cents in a container
   */
  function convertContainer(container) {
    if (!config.enabled || !isInitialized || !container) return;
    
    const elements = container.querySelectorAll ? container.querySelectorAll('[data-cents]') : [];
    elements.forEach(convertElement);
    
    if (container.hasAttribute && container.hasAttribute('data-cents')) {
      convertElement(container);
    }
  }

  /**
   * Convert a single element based on its data-cents attribute
   */
  function convertElement(element) {
    if (!config.enabled || !isInitialized) return;
    
    const centsAttr = element.getAttribute('data-cents');
    if (!centsAttr) return;
    
    // Handle cases where data-cents might be "null" or empty string if liquid wasn't output correctly
    const cents = parseInt(centsAttr.replace(/[^0-9]/g, ''), 10);
    if (isNaN(cents)) return;

    const rate = config.exchangeRates[config.currentCurrency] || 1;
    const convertedCents = Math.round(cents * rate);
    const convertedValue = convertedCents / 100;
    
    let formatted;
    // For default currency, we can try to rely on original formatMoney if available, but Intl.NumberFormat is safer across the board
    try {
      formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: config.currentCurrency,
        currencyDisplay: 'narrowSymbol'
      }).format(convertedValue);
    } catch (e) {
      formatted = '$' + convertedValue.toFixed(2);
    }
    
    const htmlContent = `<span class="money">${formatted}</span>`;
    
    // Batch DOM updates to avoid layout thrashing
    if (element.innerHTML !== htmlContent) {
      element.innerHTML = htmlContent;
    }
  }

  /**
   * Observe DOM mutations to automatically format newly added product cards (AJAX, Quick View, etc.)
   */
  function setupObserver() {
    if (observer) observer.disconnect();
    
    observer = new MutationObserver((mutations) => {
      let newNodes = [];

      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element Node
              newNodes.push(node);
            }
          });
        }
      });

      if (newNodes.length > 0) {
        requestAnimationFrame(() => {
          newNodes.forEach(node => {
            convertContainer(node);
          });
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  return {
    init,
    setCurrency,
    getCurrency,
    refresh,
    convertContainer
  };
})();